import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill } from '../../components/StatusPill'
import { CollapsibleSection } from '../../components/CollapsibleSection'
import { RelatedList } from '../../design-system/list-view/RelatedList'
import { TimelinePanel } from '../../components/timeline/TimelinePanel'
import { WidgetErrorBoundary } from '../insights/widgets/WidgetErrorBoundary'
import { getAccount } from '../../api/accounts'
import { listLegalEntities } from '../../api/legalEntities'
import { listContacts } from '../../api/contacts'
import { listBranches } from '../../api/branches'
import { listCases } from '../../api/cases'
import { ApiError } from '../../api/problem'
import { rsqlEquals, rsqlIn } from '../../lib/rsql'
import type { AccountView } from '../../types/account'
import { ACCOUNT_STATUS_LABEL, ACCOUNT_STATUS_VARIANT, ACCOUNT_TYPE_LABEL } from './columns'
import { buildLegalEntityColumns, LEGAL_ENTITY_COLUMN_ORDER } from '../legal-entities/columns'
import { buildContactColumns, CONTACT_COLUMN_ORDER } from '../contacts/columns'
import { buildBranchColumns, BRANCH_COLUMN_ORDER } from '../branches/columns'
import { buildCaseColumns, CASE_COLUMN_ORDER } from '../cases/caseColumns'
import { AccountFormModal } from './AccountFormModal'

const BRANCH_LEGAL_ENTITY_ID_CAP = 200

/**
 * The real Account primary-entity page (FR-M5.2/M5.3, docs/design/IB-49-crm-detail-timeline-design.md
 * §5.4, extended for CRM completeness — IB-93) — header → `CollapsibleSection` "Details" → real
 * `RelatedList`s for Legal Entities, Contacts, Branches, and Cases (M23 universal control set,
 * each with its own count + row links) → `TimelinePanel` (incl. its Legal Entities' and their
 * Cases' events, resolved server-side by `CrmTimelineScopeContributor`/
 * `CaseTimelineScopeContributor`, ADR-0046).
 *
 * `Branch` carries no direct `accountId` (it is a flat FK-child of `LegalEntity` — data-model
 * §3.1), so the Branches list is scoped by `legalEntityId=in=(...)` over this account's legal
 * entity ids, resolved by a small dedicated lookup (capped at
 * `BRANCH_LEGAL_ENTITY_ID_CAP`, disclosed below) rather than a per-legal-entity fan-out of
 * requests.
 */
export function AccountDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [account, setAccount] = useState<AccountView | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  const [legalEntityIds, setLegalEntityIds] = useState<string[] | null>(null)
  const [legalEntityIdsError, setLegalEntityIdsError] = useState<string | null>(null)
  const [legalEntityIdsRetryToken, setLegalEntityIdsRetryToken] = useState(0)

  const legalEntityColumns = useMemo(() => buildLegalEntityColumns(), [])
  const contactColumns = useMemo(() => buildContactColumns(), [])
  const branchColumns = useMemo(() => buildBranchColumns(), [])
  const caseColumns = useMemo(() => buildCaseColumns(), [])

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getAccount(id)
      .then(({ account: a }) => {
        if (!cancelled) setAccount(a)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not load this account.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  // Resolve this account's legal-entity ids (once), to scope the Branches
  // `RelatedList` below — `Branch` has no direct `accountId`. Capped, not
  // paginated: an account with more than `BRANCH_LEGAL_ENTITY_ID_CAP` legal
  // entities would only surface branches under the first page here — an
  // accepted, disclosed limit for this v1 (real-world accounts in this
  // synthetic dataset are far smaller), not a silent gap.
  useEffect(() => {
    if (!account) return
    let cancelled = false
    setLegalEntityIds(null)
    setLegalEntityIdsError(null)
    listLegalEntities({ filter: rsqlEquals('accountId', account.id), limit: BRANCH_LEGAL_ENTITY_ID_CAP })
      .then((page) => {
        if (!cancelled) setLegalEntityIds(page.items.map((le) => le.id))
      })
      .catch((err) => {
        if (cancelled) return
        setLegalEntityIds([])
        setLegalEntityIdsError(
          err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not load branches for this account.',
        )
      })
    return () => {
      cancelled = true
    }
  }, [account, legalEntityIdsRetryToken])

  const branchFilter = useMemo(
    () => (legalEntityIds && legalEntityIds.length > 0 ? rsqlIn('legalEntityId', legalEntityIds) : null),
    [legalEntityIds],
  )

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
        <Link to="/clients/accounts" style={{ color: 'var(--iw-text-muted)' }}>
          Accounts
        </Link>{' '}
        › {account?.name ?? id}
      </div>

      {error ? (
        <InlineAlert severity="error" action={<button type="button" className="iw-btn iw-btn--sm" onClick={() => navigate(0)}>Try again</button>}>
          {error}
        </InlineAlert>
      ) : loading ? (
        <p style={{ color: 'var(--iw-text-muted)' }}>Loading account…</p>
      ) : account ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--iw-accent)' }}>
              <Icon name="building2" size={22} />
            </span>
            <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>{account.name}</h1>
            <StatusPill variant={ACCOUNT_STATUS_VARIANT[account.status]} label={ACCOUNT_STATUS_LABEL[account.status]} />
            <span style={{ flex: 1 }} />
            <button type="button" className="iw-btn" onClick={() => setEditing(true)}>
              Edit
            </button>
          </div>

          <CollapsibleSection title="Details">
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 'var(--iw-space-4)',
                margin: 0,
              }}
            >
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Type</dt>
                <dd style={{ margin: 0 }}>{ACCOUNT_TYPE_LABEL[account.type]}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Country</dt>
                <dd style={{ margin: 0 }}>{account.country}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Region</dt>
                <dd style={{ margin: 0 }}>{account.region}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Industry</dt>
                <dd style={{ margin: 0 }}>{account.industry}</dd>
              </div>
              <div>
                <dt style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Parent account</dt>
                <dd style={{ margin: 0 }}>{account.parentAccountName ?? '—'}</dd>
              </div>
            </dl>
          </CollapsibleSection>

          <WidgetErrorBoundary title="Legal entities" icon="landmark">
            <RelatedList
              storageKeyPrefix={`iw.account-detail.${account.id}.legal-entities`}
              entityIcon="landmark"
              title="Legal entities"
              columns={legalEntityColumns}
              defaultColumnOrder={LEGAL_ENTITY_COLUMN_ORDER}
              defaultSort={[{ columnId: 'name', direction: 'asc' }]}
              searchFields={['name', 'jurisdiction']}
              searchPlaceholder="Search legal entities…"
              parentFilter={rsqlEquals('accountId', account.id)}
              fetchPage={({ filter, sort, cursor, limit }) => listLegalEntities({ filter, sort, cursor, limit })}
              getRowId={(row) => row.id}
              onRowActivate={(row) => navigate(`/clients/legal-entities/${row.id}`)}
              emptyTitle="No legal entities yet"
              emptyDescription="Create one from the Legal Entities list to get started."
            />
          </WidgetErrorBoundary>

          <WidgetErrorBoundary title="Contacts" icon="users">
            <RelatedList
              storageKeyPrefix={`iw.account-detail.${account.id}.contacts`}
              entityIcon="users"
              title="Contacts"
              columns={contactColumns}
              defaultColumnOrder={CONTACT_COLUMN_ORDER}
              defaultSort={[{ columnId: 'name', direction: 'asc' }]}
              searchFields={['name', 'email', 'title']}
              searchPlaceholder="Search contacts…"
              parentFilter={rsqlEquals('accountId', account.id)}
              fetchPage={({ filter, sort, cursor, limit }) => listContacts({ filter, sort, cursor, limit })}
              getRowId={(row) => row.id}
              onRowActivate={(row) => navigate(`/clients/contacts/${row.id}`)}
              emptyTitle="No contacts yet"
              emptyDescription="Create one from the Contacts list to get started."
            />
          </WidgetErrorBoundary>

          <WidgetErrorBoundary title="Branches" icon="map-pin">
            {legalEntityIdsError ? (
              <InlineAlert
                severity="error"
                action={
                  <button
                    type="button"
                    className="iw-btn iw-btn--sm"
                    onClick={() => setLegalEntityIdsRetryToken((n) => n + 1)}
                  >
                    Try again
                  </button>
                }
              >
                {legalEntityIdsError}
              </InlineAlert>
            ) : legalEntityIds === null ? (
              <p style={{ color: 'var(--iw-text-muted)' }}>Loading branches…</p>
            ) : branchFilter ? (
              <RelatedList
                storageKeyPrefix={`iw.account-detail.${account.id}.branches`}
                entityIcon="map-pin"
                title="Branches"
                columns={branchColumns}
                defaultColumnOrder={BRANCH_COLUMN_ORDER}
                defaultSort={[{ columnId: 'name', direction: 'asc' }]}
                searchFields={['name', 'country', 'region']}
                searchPlaceholder="Search branches…"
                parentFilter={branchFilter}
                fetchPage={({ filter, sort, cursor, limit }) => listBranches({ filter, sort, cursor, limit })}
                getRowId={(row) => row.id}
                onRowActivate={(row) => navigate(`/clients/branches/${row.id}`)}
                emptyTitle="No branches yet"
                emptyDescription="Create one from the Branches list to get started."
              />
            ) : (
              <p style={{ color: 'var(--iw-text-muted)' }}>
                No legal entities yet — branches live under a legal entity, so add one first.
              </p>
            )}
          </WidgetErrorBoundary>

          <WidgetErrorBoundary title="Cases" icon="briefcase">
            <RelatedList
              storageKeyPrefix={`iw.account-detail.${account.id}.cases`}
              entityIcon="briefcase"
              title="Cases"
              columns={caseColumns}
              defaultColumnOrder={CASE_COLUMN_ORDER}
              defaultSort={[{ columnId: 'overallStatus', direction: 'asc' }]}
              parentFilter={rsqlEquals('accountId', account.id)}
              fetchPage={({ filter, sort, cursor, limit }) => listCases({ filter, sort, cursor, limit })}
              getRowId={(row) => row.id}
              onRowActivate={(row) => navigate(`/cases/${row.id}`)}
              emptyTitle="No cases yet"
            />
          </WidgetErrorBoundary>

          <section>
            <h2 style={{ fontSize: 'var(--iw-size-md)', margin: '0 0 var(--iw-space-3)' }}>Timeline</h2>
            <TimelinePanel target={{ type: 'Account', id: account.id }} />
          </section>
        </>
      ) : null}

      {editing && account ? (
        <AccountFormModal
          mode="edit"
          editing={{ account, version: account.version }}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setAccount(updated)
            setEditing(false)
          }}
        />
      ) : null}
    </div>
  )
}
